import type {
  AuthorSuggestionsDto,
  MarkSuggestionReadDto,
  MarkSuggestionReadInput,
  RefreshResponseDto,
  SuggestionAuthorsDto,
  SuggestionDto,
  SuggestionLang,
  SuggestionListDto,
  SuggestionPreferencesDto,
  SuggestionPreferencesInput,
} from "@shelflist/shared";
import {
  MAX_FILTER_VALUES,
  SUGGESTION_AUTHORS_LIMIT_DEFAULT,
  SUGGESTION_PAGE_SIZE_DEFAULT,
  SUGGESTION_SCOPES,
} from "@shelflist/shared";
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { api, call } from "./api";
import { useErrorMessage } from "./errors";
import { featuresQuery } from "./metadata";

/** Query keys of the suggestion data; hide, unhide and a finished refresh invalidate `["suggestions"]`. */
export const suggestionKeys = {
  all: ["suggestions"] as const,
  list: (params: SuggestionListParams) => ["suggestions", "list", params] as const,
  /** Options of the author filter; no `page`, so paging the grid never refetches them. */
  authors: (params: SuggestionAuthorsParams) => ["suggestions", "authors", params] as const,
  author: (authorId: string, lang: SuggestionLang) =>
    ["suggestions", "author", authorId, lang] as const,
  preferences: ["suggestions", "preferences"] as const,
};

/** Valid author ids (1-64 characters), deduplicated, at most 20; `undefined` when none is left. */
function normalizeAuthorIds(values: readonly unknown[]): string[] | undefined {
  const ids = [
    ...new Set(
      values.filter((v): v is string => typeof v === "string" && v.length >= 1 && v.length <= 64),
    ),
  ].slice(0, MAX_FILTER_VALUES);
  return ids.length > 0 ? ids : undefined;
}

/** `author` plus the deprecated `authorId` as one list (deduplicated, capped); `undefined` when empty. */
export function mergeAuthorSearch(
  search: Pick<SuggestionsSearch, "author" | "authorId">,
): string[] | undefined {
  return normalizeAuthorIds([
    ...(search.author ?? []),
    ...(search.authorId ? [search.authorId] : []),
  ]);
}

/** Filters of the Suggestions page; the URL is the view state. */
export const suggestionsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  newOnly: z.boolean().optional().catch(undefined),
  seriesGapsOnly: z.boolean().optional().catch(undefined),
  language: z
    .union([z.string(), z.array(z.string())])
    .transform((raw) => {
      const values = [...new Set(Array.isArray(raw) ? raw : [raw])].filter((v) =>
        /^[a-z]{2,3}$/.test(v),
      );
      return values.length > 0 ? values : undefined;
    })
    .optional()
    .catch(undefined),
  /** Selected authors (repeated `author` parameter, at most 20). */
  author: z
    .union([z.string(), z.array(z.string())])
    .transform((raw) => normalizeAuthorIds(Array.isArray(raw) ? raw : [raw]))
    .optional()
    .catch(undefined),
  /** Deprecated link format; the route rewrites it to `author` (see `mergeAuthorSearch`). */
  authorId: z.string().min(1).max(64).optional().catch(undefined),
  hidden: z.literal("only").optional().catch(undefined),
  /** Absent: the server applies the user's default scope. */
  scope: z.enum(SUGGESTION_SCOPES).optional().catch(undefined),
});
export type SuggestionsSearch = z.infer<typeof suggestionsSearchSchema>;

export interface SuggestionListParams extends SuggestionsSearch {
  lang: SuggestionLang;
  pageSize?: number;
}

/** Query-string values of a list request (booleans only when set, so the URL stays short). */
function listQuery(params: SuggestionListParams) {
  return {
    page: String(params.page ?? 1),
    pageSize: String(params.pageSize ?? SUGGESTION_PAGE_SIZE_DEFAULT),
    ...filterQuery(params),
  };
}

/** The filters both endpoints share (`author` repeats; the deprecated `authorId` is merged in). */
function filterQuery(params: SuggestionsSearch & { lang: SuggestionLang }) {
  const author = mergeAuthorSearch(params);
  return {
    lang: params.lang,
    ...(params.newOnly ? { newOnly: "true" as const } : {}),
    ...(params.seriesGapsOnly ? { seriesGapsOnly: "true" as const } : {}),
    ...(params.language ? { language: params.language } : {}),
    ...(author ? { author } : {}),
    ...(params.hidden ? { hidden: params.hidden } : {}),
    ...(params.scope ? { scope: params.scope } : {}),
  };
}

/** Parameters of the author filter options: the list filters without paging, plus the search text. */
export interface SuggestionAuthorsParams extends Omit<SuggestionsSearch, "page"> {
  lang: SuggestionLang;
  /** Search text (the caller debounces it); empty means no search. */
  q?: string;
  limit?: number;
}

export const suggestionListQuery = (params: SuggestionListParams) =>
  queryOptions({
    queryKey: suggestionKeys.list(params),
    queryFn: (): Promise<SuggestionListDto> =>
      call(api.suggestions.$get({ query: listQuery(params) })),
  });

/** `GET /api/suggestions/authors`; the key has no `page` and lives under `["suggestions"]` (invalidated with the list). */
export const suggestionAuthorsQuery = (params: SuggestionAuthorsParams) => {
  const { page: _page, ...rest } = params as SuggestionAuthorsParams & { page?: number };
  const key: SuggestionAuthorsParams = { ...rest, q: rest.q?.trim() || undefined };
  return queryOptions({
    queryKey: suggestionKeys.authors(key),
    queryFn: (): Promise<SuggestionAuthorsDto> =>
      call(
        api.suggestions.authors.$get({
          query: {
            ...filterQuery(key),
            ...(key.q ? { q: key.q } : {}),
            limit: String(key.limit ?? SUGGESTION_AUTHORS_LIMIT_DEFAULT),
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
};

/** The signed-in user's language choice with the instance's allowed and the effective languages. */
export const suggestionPreferencesQuery = queryOptions({
  queryKey: suggestionKeys.preferences,
  queryFn: (): Promise<SuggestionPreferencesDto> => call(api.me["suggestion-preferences"].$get()),
});

/** Saves the given fields only (partial update: `{ defaultScope }` leaves the languages alone), invalidates every suggestion query (strip, page, author section, filters). */
export function useSaveSuggestionPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SuggestionPreferencesInput): Promise<SuggestionPreferencesDto> =>
      call(api.me["suggestion-preferences"].$put({ json: input })),
    meta: { handlesErrors: true },
    onSuccess: (saved) => {
      queryClient.setQueryData(suggestionKeys.preferences, saved);
      return queryClient.invalidateQueries({ queryKey: suggestionKeys.all });
    },
  });
}

/** Polls every 3 s while a refresh is queued or running, otherwise not at all. */
export function authorRefetchInterval(data: AuthorSuggestionsDto | undefined): number | false {
  return data && data.refresh.state !== "idle" ? 3000 : false;
}

export const authorSuggestionsQuery = (authorId: string, lang: SuggestionLang) =>
  queryOptions({
    queryKey: suggestionKeys.author(authorId, lang),
    queryFn: (): Promise<AuthorSuggestionsDto> =>
      call(api.authors[":id"].suggestions.$get({ param: { id: authorId }, query: { lang } })),
    refetchInterval: (query) => authorRefetchInterval(query.state.data),
  });

const SUGGESTION_LIST_KEY = ["suggestions", "list"] as const;
const SUGGESTION_AUTHOR_SECTION_KEY = ["suggestions", "author"] as const;

type SuggestionSnapshot = [readonly unknown[], unknown][];

/** The suggestion with `id` leaves the cached lists (hide, or show again inside the hidden view). */
function withoutSuggestion<T>(data: T, id: string): T {
  if (!data || typeof data !== "object" || !("items" in data)) return data;
  const list = data as unknown as { items: SuggestionDto[]; total?: number };
  const items = list.items.filter((item) => item.id !== id);
  if (items.length === list.items.length) return data;
  return {
    ...list,
    items,
    ...(typeof list.total === "number" ? { total: Math.max(0, list.total - 1) } : {}),
  } as T;
}

/** Hide or show a suggestion; the card leaves every cached list at once and returns on failure. */
export function useHideSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: boolean }): Promise<void> =>
      hidden
        ? call(api.suggestions[":id"].hidden.$put({ param: { id } }))
        : call(api.suggestions[":id"].hidden.$delete({ param: { id } })),
    onMutate: async ({ id }): Promise<{ snapshot: SuggestionSnapshot }> => {
      // only entries whose `items` are suggestions: the lists and the author sections, never the
      // author filter options (`["suggestions", "authors"]`, whose items are authors)
      const holders = [SUGGESTION_LIST_KEY, SUGGESTION_AUTHOR_SECTION_KEY];
      await queryClient.cancelQueries({ queryKey: suggestionKeys.all });
      const snapshot = holders.flatMap((queryKey) => queryClient.getQueriesData({ queryKey }));
      for (const queryKey of holders)
        queryClient.setQueriesData({ queryKey }, (data) => withoutSuggestion(data, id));
      return { snapshot };
    },
    onError: (_error, _vars, context) => {
      for (const [key, data] of context?.snapshot ?? []) queryClient.setQueryData(key, data);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: suggestionKeys.all }),
  });
}

/** Feature flags of the suggestions: `enabled` is `undefined` until the flags are loaded. */
export function useSuggestionFeature(): { enabled: boolean | undefined } {
  const { data } = useQuery(featuresQuery);
  return { enabled: data ? data.authorSuggestions.enabled : undefined };
}

/** Language of the UI as the suggestion API understands it. */
export function useSuggestionLang(): SuggestionLang {
  const { i18n } = useTranslation();
  return i18n.language.startsWith("de") ? "de" : "en";
}

/**
 * Hide or show a suggestion with a toast that offers "Undo" (which applies the opposite change).
 * Hiding takes effect immediately.
 */
export function useHideWithUndo() {
  const { t } = useTranslation("suggestions");
  const mutation = useHideSuggestion();
  const errorMessage = useErrorMessage();
  const { mutateAsync } = mutation;
  // The card unmounts as soon as it is hidden, so the toast and the error handling must not
  // depend on callbacks of a mounted component: use the promise and show the toast right away.
  const apply = (id: string, hidden: boolean, withUndo: boolean) => {
    if (withUndo) {
      toast(t(hidden ? "hide.hidden" : "hide.shown"), {
        action: { label: t("hide.undo"), onClick: () => apply(id, !hidden, false) },
      });
    }
    mutateAsync({ id, hidden }).catch((error: unknown) => toast.error(errorMessage(error)));
  };
  return {
    /** Hides the suggestion, or shows it again when it is hidden. */
    toggle: (suggestion: Pick<SuggestionDto, "id" | "hidden">) =>
      apply(suggestion.id, !suggestion.hidden, true),
    pending: mutation.isPending,
  };
}

export function useRefreshAuthor(authorId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<RefreshResponseDto> =>
      call(api.authors[":id"].suggestions.refresh.$post({ param: { id: authorId } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: suggestionKeys.all }),
  });
}

// --- mark as read (mark-suggestion-read) ---------------------------------------------------

/** `POST /api/suggestions/:id/read`; always sends a JSON body (`{}` for "today, no rating"). */
export function markSuggestionRead(
  id: string,
  input: MarkSuggestionReadInput = {},
): Promise<MarkSuggestionReadDto> {
  return call(
    api.suggestions[":id"].read.$post({ param: { id }, json: input }),
  ) as Promise<MarkSuggestionReadDto>;
}

/** `DELETE /api/suggestions/read-marks/:markId` (204); 409 `suggestion_read_undo_expired` after 30 minutes. */
export function undoSuggestionRead(markId: string): Promise<void> {
  return call(
    api.suggestions["read-marks"][":markId"].$delete({ param: { markId } }),
  ) as Promise<void>;
}

/** What the card passes to {@link useMarkReadWithUndo}`.mark`: the suggestion plus the dialog values. */
export interface MarkReadRequest {
  suggestion: Pick<SuggestionDto, "id" | "title" | "author">;
  /** Calendar date `YYYY-MM-DD`; omitted: today. */
  finishedAt?: string;
  /** 1-5; omitted: no rating. */
  stars?: number;
}

/** Query families that show the new state after a mark (or its undo) the next time they are shown. */
const MARK_READ_INVALIDATIONS = [
  suggestionKeys.all,
  ["reading"],
  ["books"],
  ["catalog"],
  ["authors"],
  ["my-authors-count"],
] as const;

const MARK_READ_TOAST_MS = 15_000;

/**
 * Mark a suggestion as read. `mark` resolves with the response and rejects with the `ApiError`, so
 * the dialog can show the error in place and stay open. On success the card leaves every cached
 * list, strip and author section, a toast confirms it with a book link and "Undo", and the
 * reading, catalog and suggestion queries are invalidated. Undo restores the cards from the
 * snapshot. (The removal happens after the response, not before: the dialog lives inside the card
 * and must survive a failure.)
 */
export function useMarkReadWithUndo(): {
  mark: (request: MarkReadRequest) => Promise<MarkSuggestionReadDto>;
  pending: boolean;
} {
  const { t } = useTranslation("suggestions");
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const lang = useSuggestionLang();
  const mutation = useMutation({
    mutationFn: ({ suggestion, ...input }: MarkReadRequest) =>
      markSuggestionRead(suggestion.id, { ...input, lang }),
    meta: { handlesErrors: true },
  });
  const { mutateAsync } = mutation;

  const invalidate = () =>
    Promise.all(
      MARK_READ_INVALIDATIONS.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );

  const undo = async (markId: string, snapshot: SuggestionSnapshot) => {
    try {
      await undoSuggestionRead(markId);
      for (const [key, data] of snapshot) queryClient.setQueryData(key, data);
      toast(t("markRead.undone"));
    } catch (error) {
      toast.error(errorMessage(error));
    }
    await invalidate();
  };

  const mark = async (request: MarkReadRequest): Promise<MarkSuggestionReadDto> => {
    const { suggestion } = request;
    const result = await mutateAsync(request);
    await queryClient.cancelQueries({ queryKey: suggestionKeys.all });
    const holders = [SUGGESTION_LIST_KEY, SUGGESTION_AUTHOR_SECTION_KEY];
    const snapshot = holders.flatMap((queryKey) => queryClient.getQueriesData({ queryKey }));
    for (const queryKey of holders)
      queryClient.setQueriesData({ queryKey }, (data) => withoutSuggestion(data, suggestion.id));
    toast.success(t("markRead.done"), {
      duration: MARK_READ_TOAST_MS,
      // the Toaster lives outside the router, so a plain link
      description: createElement(
        "a",
        { href: `/books/${result.bookId}`, className: "underline underline-offset-2" },
        t("markRead.openBook", { title: suggestion.title }),
      ),
      action: { label: t("markRead.undo"), onClick: () => void undo(result.markId, snapshot) },
    });
    void invalidate();
    return result;
  };
  return { mark, pending: mutation.isPending };
}
